// Which queued rows may leave the queue after a sync response.
//
// Pure, so the rule that loses data when it is wrong can be tested directly.
//
// The rule is "remove only what the server has, in the form the server has it":
//
//   - A tap or a note is immutable, so it leaves once it was sent and the server
//     did not defer or reject it.
//   - A block is not immutable: it is sent open, then sent again when it closes.
//     It leaves only when it was sent CLOSED and the stored copy still carries
//     exactly the close that was sent.
//
// The earlier rule looked at the block as stored now instead of as sent. Code
// review of PR #3 found the hole: a block sent open, closed on the phone while
// that request was in flight, was pruned when the response came back, because it
// "looked closed". Its close was never sent, the block stayed open on the server
// forever, and the next open block tripped events_one_open_block_per_session, so
// nothing more synced until End session.

import type { Block, Tap } from "./tracker.ts";

export interface SentBatch {
  /** Block id to the ended_at that was sent. null means it went up open. */
  blocks: Map<string, number | null>;
  taps: Set<string>;
  notes: Set<string>;
}

interface Queue {
  pendingBlocks: Block[];
  pendingTaps: (Tap & { eventId: string })[];
  pendingNotes: { id: string }[];
}

export function pruneConfirmed<Q extends Queue>(stored: Q, sent: SentBatch, notConfirmed: Set<string>): Q {
  const keepBlock = (b: Block) => {
    if (!sent.blocks.has(b.id) || notConfirmed.has(b.id)) return true;
    const sentEnd = sent.blocks.get(b.id)!;
    if (sentEnd === null) return true;          // only the open version reached the server
    return b.endedAt !== sentEnd;               // changed since it was sent: send again
  };
  return {
    ...stored,
    pendingBlocks: stored.pendingBlocks.filter(keepBlock),
    pendingTaps: stored.pendingTaps.filter((t) => !sent.taps.has(t.id) || notConfirmed.has(t.id)),
    pendingNotes: stored.pendingNotes.filter((n) => !sent.notes.has(n.id) || notConfirmed.has(n.id)),
  };
}

export const pendingRows = (q: Queue) => q.pendingBlocks.length + q.pendingTaps.length + q.pendingNotes.length;

/** Rows an observer should think of as not uploaded yet. The block still open in
    a live session is left out: it is always queued until it closes, so counting
    it made the badge amber from the first tap to the last. */
export const waitingRows = (q: Queue) =>
  q.pendingBlocks.filter((b) => b.endedAt !== null).length + q.pendingTaps.length + q.pendingNotes.length;
